'use client';

import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import WarningModal, { type WarningModalType } from '@/components/WarningModal';
import { stackedWarningModalLayerProps } from '@/utils/modalStacking';

interface ErrorModalContextType {
  showError: (message: string, title?: string) => void;
  showInfo: (message: string, title?: string) => void;
}

const ErrorModalContext = createContext<ErrorModalContextType | undefined>(undefined);

export const useErrorModal = () => {
  const context = useContext(ErrorModalContext);
  if (context === undefined) {
    throw new Error('useErrorModal must be used within an ErrorModalProvider');
  }
  return context;
};

/**
 * Imperative bridge so non-React modules (e.g. axios interceptors in `lib/api.ts`)
 * can trigger the global error `WarningModal` without a React hook.
 * The `ErrorModalProvider` registers its `showError` here on mount.
 */
let globalShowError: ((message: string, title?: string) => void) | null = null;

export const showGlobalErrorModal = (message: string, title: string = 'SOMETHING WENT WRONG') => {
  if (globalShowError) {
    globalShowError(message, title);
  } else if (typeof console !== 'undefined') {
    console.error('[errorModal] No ErrorModalProvider mounted to display:', title, message);
  }
};

interface ErrorModalProviderProps {
  children: ReactNode;
}

export const ErrorModalProvider: React.FC<ErrorModalProviderProps> = ({ children }) => {
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [errorTitle, setErrorTitle] = useState<string>('Error');
  const [errorModalType, setErrorModalType] = useState<WarningModalType>('error');

  const showError = (message: string, title: string = 'Error') => {
    setErrorMessage(message);
    setErrorTitle(title);
    setErrorModalType('error');
    setErrorModalOpen(true);
  };

  const showInfo = (message: string, title: string = 'Just So You Know') => {
    setErrorMessage(message);
    setErrorTitle(title);
    setErrorModalType('info');
    setErrorModalOpen(true);
  };

  useEffect(() => {
    globalShowError = showError;
    return () => {
      if (globalShowError === showError) {
        globalShowError = null;
      }
    };
  }, []);

  const handleClose = () => {
    setErrorModalOpen(false);
    setErrorMessage('');
    setErrorTitle('Error');
    setErrorModalType('error');
  };

  return (
    <ErrorModalContext.Provider value={{ showError, showInfo }}>
      {children}
      <WarningModal
        open={errorModalOpen}
        onClose={handleClose}
        type={errorModalType}
        title={errorTitle}
        message={errorMessage}
        primaryButtonText="OK"
        onPrimary={handleClose}
        {...stackedWarningModalLayerProps()}
      />
    </ErrorModalContext.Provider>
  );
};
